import { useState } from 'react';
import { Accessibility, X, ZoomIn, Contrast, RotateCcw, Volume2, Play, Pause, Square } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import { useAccessibility } from '@/contexts/AccessibilityContext';
import { useTextToSpeech } from '@/hooks/useTextToSpeech';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';

export const AccessibilityWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { textSize, highContrast, setTextSize, toggleHighContrast, resetSettings } = useAccessibility();
  const { 
    isSupported: ttsSupported, 
    isSpeaking, 
    isPaused, 
    rate, 
    setRate,
    pause,
    resume,
    stop,
    readPageContent,
    readSelectedText 
  } = useTextToSpeech();
  const { t } = useTranslation();

  const textSizes = [
    { value: 'small' as const, label: t('accessibility.small'), icon: 'A-' },
    { value: 'medium' as const, label: t('accessibility.medium'), icon: 'A' },
    { value: 'large' as const, label: t('accessibility.large'), icon: 'A+' },
    { value: 'extra-large' as const, label: t('accessibility.extraLarge'), icon: 'A++' },
  ];

  return (
    <>
      {/* Skip to content link for keyboard navigation */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:bg-primary focus:text-primary-foreground focus:px-4 focus:py-2 focus:rounded-md"
      >
        {t('accessibility.skipToContent')}
      </a>

      {/* Floating accessibility button */}
      <Button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full shadow-lg hover:scale-110 transition-transform"
        aria-label={t('accessibility.title')}
        aria-expanded={isOpen}
      >
        <Accessibility className="h-6 w-6" />
      </Button>

      {/* Accessibility panel */}
      {isOpen && (
        <Card className="fixed bottom-24 right-6 z-50 w-80 shadow-xl">
          <CardHeader className="flex flex-row items-center justify-between pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Accessibility className="h-5 w-5" />
              {t('accessibility.title')}
            </CardTitle>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsOpen(false)}
              aria-label={t('accessibility.closeMenu')}
            >
              <X className="h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Text Size Controls */}
            <div>
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <ZoomIn className="h-4 w-4" />
                {t('accessibility.textSize')}
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {textSizes.map((size) => (
                  <Button
                    key={size.value}
                    variant={textSize === size.value ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setTextSize(size.value)}
                    className={cn(
                      'justify-start',
                      textSize === size.value && 'ring-2 ring-primary'
                    )}
                    aria-label={`Set text size to ${size.label}`}
                    aria-pressed={textSize === size.value}
                  >
                    <span className="font-bold mr-2">{size.icon}</span>
                    {size.label}
                  </Button>
                ))}
              </div>
            </div>

            {/* High Contrast Toggle */}
            <div>
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                <Contrast className="h-4 w-4" />
                {t('accessibility.displayMode')}
              </h3>
              <Button
                variant={highContrast ? 'default' : 'outline'}
                size="sm"
                onClick={toggleHighContrast}
                className={cn(
                  'w-full justify-start',
                  highContrast && 'ring-2 ring-primary'
                )}
                aria-label={t('accessibility.highContrast')}
                aria-pressed={highContrast}
              >
                <Contrast className="h-4 w-4 mr-2" />
                {highContrast ? t('accessibility.highContrastOn') : t('accessibility.highContrastOff')}
              </Button>
            </div>

            {/* Text-to-Speech Controls */}
            {ttsSupported && (
              <div>
                <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
                  <Volume2 className="h-4 w-4" />
                  {t('accessibility.textToSpeech')}
                </h3>
                
                {/* Speech controls */}
                <div className="space-y-2">
                  {!isSpeaking ? (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={readPageContent}
                        className="w-full justify-start"
                        aria-label={t('accessibility.readPage')}
                      >
                        <Play className="h-4 w-4 mr-2" />
                        {t('accessibility.readPage')}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={readSelectedText}
                        className="w-full justify-start"
                        aria-label={t('accessibility.readSelected')}
                      >
                        <Play className="h-4 w-4 mr-2" />
                        {t('accessibility.readSelected')}
                      </Button>
                    </>
                  ) : (
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={isPaused ? resume : pause}
                        aria-label={isPaused ? t('accessibility.resume') : t('accessibility.pause')}
                      >
                        {isPaused ? (
                          <>
                            <Play className="h-4 w-4 mr-2" />
                            {t('accessibility.resume')}
                          </>
                        ) : (
                          <>
                            <Pause className="h-4 w-4 mr-2" />
                            {t('accessibility.pause')}
                          </>
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={stop}
                        aria-label={t('accessibility.stop')}
                      >
                        <Square className="h-4 w-4 mr-2" />
                        {t('accessibility.stop')}
                      </Button>
                    </div>
                  )}

                  {/* Speech rate control */}
                  <div className="pt-2">
                    <label className="text-xs text-muted-foreground mb-2 block">
                      {t('accessibility.speechRate')}: {rate.toFixed(1)}x
                    </label>
                    <Slider
                      value={[rate]}
                      onValueChange={(value) => setRate(value[0])}
                      min={0.5}
                      max={2}
                      step={0.1}
                      className="w-full"
                      aria-label={t('accessibility.speechRate')}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Reset Button */}
            <Button
              variant="secondary"
              size="sm"
              onClick={resetSettings}
              className="w-full"
              aria-label={t('accessibility.reset')}
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              {t('accessibility.reset')}
            </Button>

            {/* Keyboard Navigation Info */}
            <div className="text-xs text-muted-foreground border-t pt-3">
              <p className="font-semibold mb-1">{t('accessibility.keyboardNav')}</p>
              <ul className="space-y-1 list-disc list-inside">
                <li>Tab: Navigate forward</li>
                <li>Shift+Tab: Navigate backward</li>
                <li>Enter/Space: Activate</li>
                <li>Esc: Close dialogs</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
};
